package com.patronat.top.features.dashboard.presentation.reviewmoderation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.dto.ReviewAiDecision
import com.patronat.top.dto.ReviewAiSeverity
import com.patronat.top.dto.ReviewAiSource
import com.patronat.top.dto.ReviewAiStatus
import com.patronat.top.dto.ReviewResponse
import com.patronat.top.dto.ReviewStatus
import com.patronat.top.ui.atoms.AppButton
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.atoms.ButtonType
import com.patronat.top.ui.molecules.filters.FilterOption
import com.patronat.top.ui.molecules.pagination.TablePagination
import com.patronat.top.ui.molecules.status.StatusBadge
import com.patronat.top.ui.molecules.status.StatusBadgeColors
import com.patronat.top.ui.organisms.EmptyStateContent
import com.patronat.top.ui.organisms.UsersControlPanel
import com.patronat.top.ui.theme.AccentPink
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.AvatarPlaceholderGray
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.LabelLarge
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.Primary700
import com.patronat.top.ui.theme.White
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.koinInject
import top.features.dashboard.generated.resources.Res
import top.features.dashboard.generated.resources.img_empty_ai_moderation

@Composable
fun ReviewAiModerationScreen(
    modifier: Modifier = Modifier
) {
    val viewModel = koinInject<ReviewAiModerationViewModel>()
    val state by viewModel.viewState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.onEvent(ReviewAiModerationEvent.Initialize)
    }

    val statusOptions = listOf(
        FilterOption(ReviewModerationStatusFilterPreset.NEW.id, "Нові"),
        FilterOption(ReviewModerationStatusFilterPreset.PUBLISHED.id, "Опубліковані"),
        FilterOption(ReviewModerationStatusFilterPreset.REJECTED.id, "Відхилені")
    )
    val selectedStatusOptions = statusOptions.filter { option ->
        state.selectedStatusFilterIds.any { it == option.id }
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        UsersControlPanel(
            searchQuery = state.searchQuery,
            onSearchQueryChange = { query ->
                viewModel.onEvent(ReviewAiModerationEvent.OnSearchChanged(query))
            },
            searchPlaceholder = "Шукати відгук, заклад, автора",
            filterLabel = "Статус:",
            filterOptions = statusOptions,
            selectedFilterOptions = selectedStatusOptions,
            onFilterChange = { selected ->
                viewModel.onEvent(ReviewAiModerationEvent.OnStatusFilterChanged(selected.map { it.id }))
            },
            onAddClick = {},
            addButtonText = "",
            isAddButtonVisible = false,
            modifier = Modifier.padding(bottom = 16.dp)
        )

        when {
            state.isLoading && state.reviews.isEmpty() -> {
                Box(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = Primary600)
                }
            }
            state.isEmpty -> {
                EmptyStateContent(
                    image = painterResource(Res.drawable.img_empty_ai_moderation),
                    title = if (state.searchQuery.isBlank()) {
                        "Поки що немає відгуків для модерації."
                    } else {
                        "Нічого не знайдено."
                    },
                    description = "Нові AI-перевірені відгуки з'являться тут.",
                    buttonText = "",
                    onButtonClick = {},
                    buttonIcon = AppIcons.Plus,
                    buttonNeeded = false,
                    modifier = Modifier.weight(1f).fillMaxWidth()
                )
            }
            else -> {
                LazyColumn(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    contentPadding = PaddingValues(bottom = 8.dp)
                ) {
                    items(state.reviews, key = { it.id }) { review ->
                        ReviewModerationCard(
                            review = review,
                            venueName = state.venueNamesById[review.venueId],
                            isActionLoading = state.actionReviewId == review.id,
                            isAnyActionLoading = state.actionReviewId != null,
                            onApprove = {
                                viewModel.onEvent(ReviewAiModerationEvent.OnApproveClicked(review.id))
                            },
                            onReject = {
                                viewModel.onEvent(ReviewAiModerationEvent.OnRejectClicked(review.id))
                            }
                        )
                    }
                }

                TablePagination(
                    currentPage = state.currentPage,
                    totalItems = state.totalCount.toInt(),
                    pageSize = state.pageSize,
                    onPageChange = { page -> viewModel.onEvent(ReviewAiModerationEvent.OnPageChanged(page)) },
                    onPageSizeChange = { pageSize ->
                        viewModel.onEvent(ReviewAiModerationEvent.OnPageSizeChanged(pageSize))
                    },
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun ReviewModerationCard(
    review: ReviewResponse,
    venueName: String?,
    isActionLoading: Boolean,
    isAnyActionLoading: Boolean,
    onApprove: () -> Unit,
    onReject: () -> Unit
) {
    val cardBorderColor = when (review.status) {
        ReviewStatus.REJECTED -> AccentPink
        ReviewStatus.PUBLISHED -> MineShaft100
        ReviewStatus.ON_MODERATION -> MineShaft100
    }
    val photos = review.photos.mapNotNull { it.publicUrl?.takeIf(String::isNotBlank) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(BorderStroke(1.dp, cardBorderColor), RoundedCornerShape(12.dp))
            .background(White, RoundedCornerShape(12.dp))
            .padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top
        ) {
            ReviewAuthorAvatar(
                name = review.author?.displayName,
                avatarUrl = review.author?.avatarUrl
            )

            Spacer(modifier = Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                ReviewHeader(
                    authorName = review.author?.displayName.orEmpty().ifBlank { "Користувач" },
                    venueName = venueName ?: "Заклад",
                    createdAt = review.createdAt,
                    rating = review.rating
                )

                Spacer(modifier = Modifier.height(10.dp))

                Text(
                    text = review.text?.takeIf(String::isNotBlank) ?: "Відгук без тексту",
                    style = BodyLarge.copy(
                        color = MineShaft900,
                        lineHeight = 24.sp
                    ),
                    maxLines = 5,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            AiReasonBadge(review = review)
        }

        if (review.aiFindings.isNotEmpty() || review.aiError?.isNotBlank() == true) {
            Spacer(modifier = Modifier.height(16.dp))
            AiFindingsBlock(review = review)
        }

        if (photos.isNotEmpty()) {
            Spacer(modifier = Modifier.height(16.dp))
            ReviewPhotosBlock(photos = photos)
        }

        Spacer(modifier = Modifier.height(16.dp))

        ReviewActions(
            status = review.status,
            isActionLoading = isActionLoading,
            isAnyActionLoading = isAnyActionLoading,
            onApprove = onApprove,
            onReject = onReject
        )
    }
}

@Composable
private fun ReviewAuthorAvatar(
    name: String?,
    avatarUrl: String?
) {
    val resolvedName = name.orEmpty().ifBlank { "Користувач" }
    val initials = remember(resolvedName) { resolvedName.toInitials() }

    Box(
        modifier = Modifier
            .size(48.dp)
            .clip(CircleShape)
            .background(AvatarPlaceholderGray),
        contentAlignment = Alignment.Center
    ) {
        if (!avatarUrl.isNullOrBlank()) {
            AppNetworkImage(
                model = avatarUrl,
                contentDescription = resolvedName,
                modifier = Modifier.fillMaxSize().clip(CircleShape),
                contentScale = ContentScale.Crop
            )
        } else {
            Text(
                text = initials,
                style = LabelLarge.copy(
                    color = White,
                    fontWeight = FontWeight.Medium
                ),
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
private fun ReviewHeader(
    authorName: String,
    venueName: String,
    createdAt: Instant,
    rating: Int
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(
            text = authorName,
            style = LabelLarge.copy(
                color = MineShaft950,
                fontWeight = FontWeight.SemiBold
            ),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        Text(
            text = "  ->  ",
            style = BodyMedium.copy(color = MineShaft400)
        )
        Text(
            text = venueName,
            style = BodyMedium.copy(color = MineShaft500),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }

    Spacer(modifier = Modifier.height(6.dp))

    Row(verticalAlignment = Alignment.CenterVertically) {
        val filledStars = rating.toFiveStarRating()
        repeat(5) { index ->
            Icon(
                painter = AppIcons.RatingStarFilled,
                contentDescription = null,
                tint = if (index < filledStars) Primary600 else MineShaft200,
                modifier = Modifier.size(16.dp)
            )
            Spacer(modifier = Modifier.width(2.dp))
        }
        Spacer(modifier = Modifier.width(10.dp))
        Text(
            text = createdAt.toDisplayDateTime(),
            style = BodyMedium.copy(color = MineShaft500)
        )
    }
}

@Composable
private fun AiReasonBadge(review: ReviewResponse) {
    val text = review.aiBadgeText()
    val colors = when {
        review.aiStatus == ReviewAiStatus.ERROR -> StatusBadgeColors(
            bgColor = AccentPink.copy(alpha = 0.08f),
            contentColor = AccentPink,
            borderColor = AccentPink.copy(alpha = 0.35f)
        )
        review.aiDecision == ReviewAiDecision.WARN -> StatusBadgeColors(
            bgColor = Primary600.copy(alpha = 0.10f),
            contentColor = Primary700,
            borderColor = Primary600.copy(alpha = 0.25f)
        )
        review.aiDecision == ReviewAiDecision.FAIL -> StatusBadgeColors(
            bgColor = AccentPink.copy(alpha = 0.08f),
            contentColor = AccentPink,
            borderColor = AccentPink.copy(alpha = 0.35f)
        )
        else -> StatusBadgeColors(
            bgColor = MineShaft50,
            contentColor = MineShaft500,
            borderColor = MineShaft200
        )
    }

    StatusBadge(
        text = text,
        colors = colors,
        showDot = false,
        cornerRadius = 8.dp,
        horizontalPadding = 12.dp,
        verticalPadding = 6.dp
    )
}

@Composable
private fun AiFindingsBlock(review: ReviewResponse) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, AccentPink.copy(alpha = 0.18f), RoundedCornerShape(12.dp))
            .background(AccentPink.copy(alpha = 0.04f), RoundedCornerShape(12.dp))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = "AI-модерація",
            style = LabelLarge.copy(color = MineShaft950)
        )

        review.aiError?.takeIf(String::isNotBlank)?.let { error ->
            FindingText(
                title = "Помилка перевірки",
                description = error,
                severity = ReviewAiSeverity.WARNING,
                source = ReviewAiSource.SYSTEM
            )
        }

        review.aiFindings.forEach { finding ->
            FindingText(
                title = finding.fieldPath.toFindingTitle(),
                description = finding.reason,
                severity = finding.severity,
                source = finding.source
            )
        }
    }
}

@Composable
private fun FindingText(
    title: String,
    description: String,
    severity: ReviewAiSeverity,
    source: ReviewAiSource
) {
    val severityColor = if (severity == ReviewAiSeverity.CRITICAL) AccentPink else Primary700

    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = title,
                style = BodyMedium.copy(
                    color = severityColor,
                    fontWeight = FontWeight.SemiBold
                )
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = source.toUiText(),
                style = BodyMedium.copy(
                    color = MineShaft400,
                    fontSize = 12.sp
                )
            )
        }
        Text(
            text = description,
            style = BodyMedium.copy(
                color = MineShaft900,
                lineHeight = 20.sp
            )
        )
    }
}

@Composable
private fun ReviewPhotosBlock(photos: List<String>) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, MineShaft100, RoundedCornerShape(12.dp))
            .padding(14.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                painter = AppIcons.Photo,
                contentDescription = null,
                tint = MineShaft500,
                modifier = Modifier.size(18.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = "Фото",
                style = LabelLarge.copy(color = MineShaft900)
            )
        }

        Spacer(modifier = Modifier.height(12.dp))

        Row(
            modifier = Modifier.horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            photos.forEach { url ->
                AppNetworkImage(
                    model = url,
                    contentDescription = "Фото відгуку",
                    modifier = Modifier
                        .size(width = 184.dp, height = 124.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(MineShaft50),
                    contentScale = ContentScale.Crop,
                    placeholder = AppIcons.VenueImagePlaceholder
                )
            }
        }
    }
}

@Composable
private fun ReviewActions(
    status: ReviewStatus,
    isActionLoading: Boolean,
    isAnyActionLoading: Boolean,
    onApprove: () -> Unit,
    onReject: () -> Unit
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (status != ReviewStatus.PUBLISHED) {
            AppButton(
                text = "Опублікувати",
                onClick = onApprove,
                type = ButtonType.BrownFilled,
                enabled = !isAnyActionLoading,
                isLoading = isActionLoading,
                leadingIcon = AppIcons.Check,
                modifier = Modifier.widthIn(min = 188.dp)
            )
        }

        if (status != ReviewStatus.REJECTED) {
            AppButton(
                text = "Відхилити",
                onClick = onReject,
                type = ButtonType.ErrorOutline,
                enabled = !isAnyActionLoading,
                isLoading = isActionLoading,
                leadingIcon = AppIcons.Close,
                modifier = Modifier.widthIn(min = 188.dp)
            )
        }
    }
}

private fun ReviewResponse.aiBadgeText(): String {
    if (aiStatus == ReviewAiStatus.ERROR) return "AI помилка"

    val hasTextIssue = aiFindings.any { it.fieldPath.contains("text", ignoreCase = true) }
    val hasPhotoIssue = aiFindings.any {
        it.fieldPath.contains("photo", ignoreCase = true) ||
            it.fieldPath.contains("image", ignoreCase = true)
    }

    return when {
        hasTextIssue && hasPhotoIssue -> "Весь відгук"
        hasPhotoIssue -> "Фото"
        hasTextIssue -> "Текст"
        aiDecision == ReviewAiDecision.FAIL -> "Порушення"
        aiDecision == ReviewAiDecision.WARN -> "Попередження"
        else -> "AI перевірено"
    }
}

private fun String.toFindingTitle(): String {
    return when {
        contains("text", ignoreCase = true) -> "Текст"
        contains("photo", ignoreCase = true) || contains("image", ignoreCase = true) -> "Фото"
        else -> "Відгук"
    }
}

private fun ReviewAiSource.toUiText(): String {
    return when (this) {
        ReviewAiSource.REKOGNITION -> "Фото"
        ReviewAiSource.BEDROCK -> "Текст"
        ReviewAiSource.SYSTEM -> "Система"
    }
}

private fun String.toInitials(): String {
    return trim()
        .split(" ")
        .mapNotNull { it.firstOrNull()?.uppercaseChar()?.toString() }
        .take(2)
        .joinToString("")
        .ifBlank { "К" }
}

private fun Int.toFiveStarRating(): Int {
    return coerceIn(1, 10).let { (it + 1) / 2 }.coerceIn(1, 5)
}

private fun Instant.toDisplayDateTime(): String {
    val dateTime = toLocalDateTime(TimeZone.currentSystemDefault())
    val month = dateTime.monthNumber.toString().padStart(2, '0')
    val day = dateTime.dayOfMonth.toString().padStart(2, '0')
    val hour = dateTime.hour.toString().padStart(2, '0')
    val minute = dateTime.minute.toString().padStart(2, '0')
    return "${dateTime.year}-$month-$day $hour:$minute"
}
